<template>
  <list-page-shell>
    <template #filters>
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item :label="$t('contractListPage.keywordLabel')">
          <el-input
            v-model="filters.keyword"
            clearable
            :placeholder="$t('contractListPage.searchPh')"
            style="width:240px;"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item :label="$t('contractListPage.statusLabel')">
          <el-select
            v-model="filters.contractStatusCode"
            clearable
            :placeholder="$t('common.pleaseSelect')"
            style="width:140px;"
          >
            <el-option
              v-for="opt in statusFilterOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" :disabled="loading" @click="handleReset">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </template>

    <el-table
      :data="list"
      v-loading="loading"
      stripe
      border
      class="contract-table"
      :empty-text="emptyText"
    >
      <el-table-column prop="contractNo" :label="$t('grid.number')" width="150" show-overflow-tooltip />
      <el-table-column prop="contractTitle" :label="$t('grid.title')" min-width="160" show-overflow-tooltip />
      <el-table-column :label="$t('grid.quote')" width="130" show-overflow-tooltip>
        <template slot-scope="s">
          <template v-if="s.row.linkedQuoteNo">{{ s.row.linkedQuoteNo }}</template>
          <span v-else class="text-muted">—</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('common.amount')" min-width="168" align="right" show-overflow-tooltip>
        <template slot-scope="s">
          <span class="contract-amt-cell">
            <span>{{ formatUsd(contractRowTotal(s.row)) }}</span>
            <span
              v-if="contractRowHasChange(s.row)"
              class="contract-amt-cell__parts"
            >（{{ formatUsd(s.row.quoteTotalAmount) }} + {{ formatUsd(s.row.approvedChangeOrderAmount) }}）</span>
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('grid.template')" min-width="100" show-overflow-tooltip>
        <template slot-scope="s">
          {{ s.row.contractTemplateName || '—' }}
        </template>
      </el-table-column>
      <el-table-column :label="$t('grid.project')" width="100" align="center">
        <template slot-scope="s">
          <el-tag v-if="s.row.linkedProjectId" type="success" size="mini">{{ $t('contractListPage.linked') }}</el-tag>
          <span v-else class="text-muted">{{ $t('contractListPage.notCreated') }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('common.status')" width="100" align="center">
        <template slot-scope="s">
          <el-tag :type="contractStatusTagType(s.row.contractStatusCode)" size="mini" effect="plain">
            {{ labelContractStatus(s.row.contractStatusCode) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="$t('grid.createdAt')" width="190">
        <template slot-scope="s">{{ formatDisplayDateTime(s.row.createdAt) || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t('common.actions')" width="120" fixed="right" align="center">
        <template slot-scope="s">
          <el-button type="text" size="small" @click="openContractDetail(s.row.id)">{{ $t('common.details') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="list-pager">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :page-sizes="[10, 20, 50]"
        :total="total"
        :page-size="pageSize"
        :current-page="pageNo"
        @size-change="onSizeChange"
        @current-change="p => { pageNo = p; load() }"
      />
    </div>

  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import { getContractPage } from '@/api/contract'
import { contractStatusTagType, labelContractStatus } from '@/utils/crmLabels'
import { formatUsd } from '@/utils/formatUsd'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'
import { contractDetailLocation } from '@/utils/contractDetailNav'

const CONTRACT_STATUS_CODES = [1, 2, 3, 4, 5]

export default {
  name: 'ContractList',
  components: { ListPageShell },
  data() {
    return {
      list: [],
      total: 0,
      pageNo: 1,
      pageSize: 10,
      loading: false,
      filters: {
        keyword: '',
        contractStatusCode: null
      }
    }
  },
  computed: {
    statusFilterOptions() {
      return CONTRACT_STATUS_CODES.map(code => ({
        value: code,
        label: this.$t('enum.contractStatus.' + code)
      }))
    },
    emptyText() {
      return this.loading ? this.$t('contractListPage.emptyLoading') : this.$t('contractListPage.emptyHint')
    }
  },
  created() {
    this.load()
  },
  methods: {
    formatUsd,
    formatDisplayDateTime,
    labelContractStatus,
    contractStatusTagType,
    contractRowTotal(row) {
      if (!row) return null
      return row.contractAmountTotal != null ? row.contractAmountTotal : row.quoteTotalAmount
    },
    contractRowHasChange(row) {
      if (!row) return false
      const n = Number(row.approvedChangeOrderAmount)
      return !Number.isNaN(n) && n !== 0
    },
    openContractDetail(contractId) {
      this.$router.push(contractDetailLocation(contractId, this.$route.fullPath))
    },
    handleSearch() {
      this.pageNo = 1
      this.load()
    },
    handleReset() {
      this.filters = { keyword: '', contractStatusCode: null }
      this.pageNo = 1
      this.load()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.pageNo = 1
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const params = {
          pageNo: this.pageNo,
          pageSize: this.pageSize
        }
        const k = (this.filters.keyword || '').trim()
        if (k) params.keyword = k
        const st = this.filters.contractStatusCode
        if (st != null && st !== '') {
          params.contractStatusCode = st
        }
        const r = await getContractPage(params)
        const d = r.data || r
        this.list = d.records || []
        this.total = d.total || 0
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.contract-table {
  width: 100%;
}

.text-muted {
  color: var(--color-text-sub);
}

.contract-amt-cell {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: flex-end;
  gap: 4px 6px;
}

.contract-amt-cell__parts {
  font-size: 12px;
  color: var(--color-text-sub);
}

.list-pager {
  margin-top: 16px;
  text-align: right;
}
</style>
